import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from '@app/app.component';
import { Routing } from '@app/app.routing';
import { RolesService } from '@app/core/services/roles.service';
import { MaterialAppModule } from '@app/ngmaterial.module';
import { AppConfig } from '@config/app.config';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { AuthenticationInterceptor } from '@core/interceptors/authentication.interceptor';
import { MenuComponent } from '@core/menu/menu.component';
import { AccountingEventsService } from '@core/services/accounting-events.service';
import { AccountingGroupsService } from '@core/services/accounting-groups.service';
import { AccountingService } from '@core/services/accounting.service';
import { AmlLimitsService } from '@core/services/aml-limits.service';
import { AuthService } from '@core/services/auth.service';
import { ChannelsService } from '@core/services/channels.service';
import { CommissionsService } from '@core/services/commissions.service';
import { ConstantsService } from '@core/services/constants.service';
import { CustomersService } from '@core/services/customers.service';
import { GeneralsService } from '@core/services/generals.service';
import { PaymentMethodsService } from '@core/services/payment-methods.service';
import { RulesService } from '@core/services/rules.service';
import { SegmentsService } from '@core/services/segments.service';
import { StatusService } from '@core/services/status.service';
import { TemplatesService } from '@core/services/templates.service';
import { TransfersService } from '@core/services/transfers.service';
import { UsersService } from '@core/services/users.service';
import { ValidationsService } from '@core/services/validations.service';
import { environment } from '@environments/environment';
import {
  AccountingEventsEditComponent,
} from '@modules/accounting-events/accounting-events-edit/accounting-events-edit.component';
import {
  AccountingEventsListComponent,
} from '@modules/accounting-events/accounting-events-list/accounting-events-list.component';
import {
  AccountingGroupsEditComponent,
} from '@modules/accounting-groups/accounting-groups-edit/accounting-groups-edit.component';
import {
  AccountingGroupsListComponent,
} from '@modules/accounting-groups/accounting-groups-list/accounting-groups-list.component';
import {
  BalanceInterfaceDownloadComponent,
} from '@modules/accounting/balance-interface-download/balance-interface-download.component';
import {
  MasterTransfersDownloadComponent,
} from '@modules/accounting/master-transfers-download/master-transfers-download.component';
import {
  MovementInterfaceDownloadComponent,
} from '@modules/accounting/movement-interface-download/movement-interface-download.component';
import {
  TransactionsEventsDownloadComponent,
} from '@modules/accounting/transactions-events-download/transactions-events-download.component';
import {
  TransactionsEventsLogDownloadComponent,
} from '@modules/accounting/transactions-events-log-download/transactions-events-log-download.component';
import {TotalsCostsDownloadComponent} from '@modules/accounting/totals-costs-download/totals-costs-download.component';
import { AmlLimitsEditComponent } from '@modules/aml-limits/aml-limits-edit/aml-limits-edit.component';
import { AmlLimitsListComponent } from '@modules/aml-limits/aml-limits-list/aml-limits-list.component';
import { ChannelsEditComponent } from '@modules/channels/channels-edit/channels-edit.component';
import { ChannelsListComponent } from '@modules/channels/channels-list/channels-list.component';
import { CommissionsEditComponent } from '@modules/commissions/commissions-edit/commissions-edit.component';
import { CommissionsListComponent } from '@modules/commissions/commissions-list/commissions-list.component';
import { ConstantsEditComponent } from '@modules/constants/constants-edit/constants-edit.component';
import { ConstantsListComponent } from '@modules/constants/constants-list/constants-list.component';
import { CustomersFilterComponent } from '@modules/customers/customers-filter/customers-filter.component';
import { CustomersListComponent } from '@modules/customers/customers-list/customers-list.component';
import { HomeComponent } from '@modules/home/home.component';
import { LoginComponent } from '@modules/login/login.component';
import { PaymentMethodsEditComponent } from '@modules/payment-methods/payment-methods-edit/payment-methods-edit.component';
import { PaymentMethodsListComponent } from '@modules/payment-methods/payment-methods-list/payment-methods-list.component';
import { RolesEditComponent } from '@modules/roles/roles-edit/roles-edit.component';
import { RolesListComponent } from '@modules/roles/roles-list/roles-list.component';
import { RulesEditComponent } from '@modules/rules/rules-edit/rules-edit.component';
import { RulesListComponent } from '@modules/rules/rules-list/rules-list.component';
import { SegmentsEditComponent } from '@modules/segments/segments-edit/segments-edit.component';
import { SegmentsListComponent } from '@modules/segments/segments-list/segments-list.component';
import { StatusEditComponent } from '@modules/status/status-edit/status-edit.component';
import { StatusListComponent } from '@modules/status/status-list/status-list.component';
import { TemplatesEditComponent } from '@modules/templates/templates-edit/templates-edit.component';
import { TemplatesListComponent } from '@modules/templates/templates-list/templates-list.component';
import {
  TransactionalEventsEditComponent,
} from '@modules/transactional-events/transactional-events-edit/transactional-events-edit.component';
import {
  TransactionalEventsListComponent,
} from '@modules/transactional-events/transactional-events-list/transactional-events-list.component';
import {
  TransfersCustomerFilterComponent,
} from '@modules/transfers/transfers-customer-filter/transfers-customer-filter.component';
import {
  TransfersCustomerListComponent,
} from '@modules/transfers/transfers-customer-list/transfers-customer-list.component';
import { TransfersEditComponent } from '@modules/transfers/transfers-edit/transfers-edit.component';
import { TransfersListComponent } from '@modules/transfers/transfers-list/transfers-list.component';
import {
  TransfersReferenceFilterComponent,
} from '@modules/transfers/transfers-reference-filter/transfers-reference-filter.component';
import { UsersEditComponent } from '@modules/users/users-edit/users-edit.component';
import { UsersListComponent } from '@modules/users/users-list/users-list.component';
import { OfPipe } from '@shared/pipes/of.pipe';
import { PhonePipe } from '@shared/pipes/phone.pipe';
import { ScriptPipe } from '@shared/pipes/script.pipe';
import { SharedModule } from '@shared/shared.module';
import { UtilsModule } from '@shared/utils';
import { BcAdalAngularModule } from 'bc-adal-angular';
import { NgIdleModule } from 'ng2-idle-core';

@NgModule({
  declarations: [
    AppComponent,
    MenuComponent,
    HomeComponent,
    LoginComponent,
    AccountingEventsEditComponent,
    AccountingEventsListComponent,
    AccountingGroupsEditComponent,
    AccountingGroupsListComponent,
    AmlLimitsEditComponent,
    AmlLimitsListComponent,
    BalanceInterfaceDownloadComponent,
    ChannelsEditComponent,
    ChannelsListComponent,
    CommissionsEditComponent,
    CommissionsListComponent,
    ConstantsEditComponent,
    ConstantsListComponent,
    CustomersFilterComponent,
    CustomersListComponent,
    MasterTransfersDownloadComponent,
    MovementInterfaceDownloadComponent,
    PaymentMethodsEditComponent,
    PaymentMethodsListComponent,
    RolesListComponent,
    RolesEditComponent,
    RulesEditComponent,
    RulesListComponent,
    SegmentsEditComponent,
    SegmentsListComponent,
    StatusEditComponent,
    StatusListComponent,
    TemplatesEditComponent,
    TemplatesListComponent,
    TransactionsEventsDownloadComponent,
    TransactionsEventsLogDownloadComponent,
    TotalsCostsDownloadComponent,
    TransactionalEventsListComponent,
    TransactionalEventsEditComponent,
    TransfersCustomerFilterComponent,
    TransfersCustomerListComponent,
    TransfersEditComponent,
    TransfersListComponent,
    TransfersReferenceFilterComponent,
    UsersEditComponent,
    UsersListComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    MaterialAppModule,
    ReactiveFormsModule,
    Routing,
    SharedModule,
    NgIdleModule.forRoot(),
    BcAdalAngularModule.forRoot(environment.adalConfig),
    UtilsModule.forRoot()
  ],
  providers: [
    AppConfig,
    GeneralsService,
    ValidationsService,
    OfPipe,
    PhonePipe,
    ScriptPipe,
    AccountingService,
    AccountingEventsService,
    AccountingGroupsService,
    AmlLimitsService,
    AuthService,
    AuthenticationService,
    ChannelsService,
    CommissionsService,
    ConstantsService,
    CustomersService,
    PaymentMethodsService,
    RolesService,
    RulesService,
    SegmentsService,
    StatusService,
    TemplatesService,
    TransfersService,
    UsersService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptor,
      multi: true
    }
  ],
  entryComponents: [
    AccountingEventsEditComponent,
    AccountingGroupsEditComponent,
    AmlLimitsEditComponent,
    ChannelsEditComponent,
    CommissionsEditComponent,
    ConstantsEditComponent,
    CustomersListComponent,
    PaymentMethodsEditComponent,
    RolesEditComponent,
    RulesEditComponent,
    SegmentsEditComponent,
    StatusEditComponent,
    TemplatesEditComponent,
    TransactionalEventsEditComponent,
    TransfersCustomerListComponent,
    TransfersEditComponent,
    TransfersListComponent,
    UsersEditComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
